/**
 * "Save Login Info" — per-device remembered accounts.
 *
 * Only non-sensitive identity is stored (never the password or JWT): enough to
 * render a "Saved Accounts" chip on the login screen and pre-fill the email.
 * Lives in `localStorage` so it survives closing the tab/browser, unlike the
 * session token which is tab-scoped.
 */

const STORAGE_KEY = "swastik_saved_logins";
const MAX_ACCOUNTS = 6;

const safeLocal = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    // Safari private mode / blocked storage.
    return null;
  }
};

export function getSavedLogins() {
  const store = safeLocal();
  if (!store) return [];
  try {
    const raw = store.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry) => entry && typeof entry.email === "string" && entry.email);
  } catch {
    return [];
  }
}

function writeSavedLogins(list) {
  const store = safeLocal();
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ACCOUNTS)));
  } catch {
    /* storage full/unavailable — remembering is best-effort */
  }
}

/** Add or refresh an account, moving it to the front of the list. */
export function rememberLogin({ email, name, role, avatar }) {
  if (!email) return;
  const entry = {
    email: String(email).trim().toLowerCase(),
    name: name || "",
    role: role || "staff",
    avatar: avatar || "",
    savedAt: Date.now(),
  };
  const next = getSavedLogins().filter((item) => item.email !== entry.email);
  next.unshift(entry);
  writeSavedLogins(next);
}

export function forgetLogin(email) {
  if (!email) return;
  const needle = String(email).trim().toLowerCase();
  writeSavedLogins(getSavedLogins().filter((item) => item.email !== needle));
}

export function isLoginRemembered(email) {
  if (!email) return false;
  const needle = String(email).trim().toLowerCase();
  return getSavedLogins().some((item) => item.email === needle);
}
